import type { AccusationPhaseState } from "@cold-case/shared/accusation-phase";
import type { Socket } from "socket.io-client";

export const ACCUSATION_SOCKET = {
  start: "game:accusation_start",
  vote: "game:accusation_vote",
  pick: "game:accusation_pick",
  adopt: "game:accusation_adopt",
  warrant: "game:accusation_warrant",
  phase: "game:accusation_phase",
  cancelled: "game:accusation_cancelled",
} as const;

const START_ERROR_MESSAGES: Record<string, string> = {
  insufficient_evidence: "You need more discovered clues before starting an accusation.",
  no_suspects: "Interview at least one suspect in the world first.",
  phase_active: "An accusation is already in progress.",
  invalid_status: "You cannot start an accusation right now.",
  solo_only: "Solo games use the standard Accuse tab.",
  forbidden: "Could not start the accusation.",
};

export type AccusationPhaseSyncHandlers = {
  onPhase: (phase: AccusationPhaseState | null, error?: string) => void;
  onCancelled: (reason: string) => void;
};

export function bindAccusationPhaseSync(
  socket: Socket,
  handlers: AccusationPhaseSyncHandlers
): () => void {
  const onPhase = (payload: { phase: AccusationPhaseState | null; error?: string }) => {
    if (payload?.error) {
      handlers.onPhase(null, START_ERROR_MESSAGES[payload.error] ?? "Could not start accusation.");
      return;
    }
    handlers.onPhase(payload?.phase ?? null);
  };
  const onCancelled = (payload: { reason?: string }) => {
    handlers.onCancelled(payload?.reason ?? "vote_rejected");
  };
  socket.on(ACCUSATION_SOCKET.phase, onPhase);
  socket.on(ACCUSATION_SOCKET.cancelled, onCancelled);
  return () => {
    socket.off(ACCUSATION_SOCKET.phase, onPhase);
    socket.off(ACCUSATION_SOCKET.cancelled, onCancelled);
  };
}

export function emitAccusationStart(socket: Socket | null, sessionId: string): void {
  socket?.emit(ACCUSATION_SOCKET.start, { sessionId });
}

export function emitAccusationVote(
  socket: Socket | null,
  sessionId: string,
  vote: "yes" | "no"
): void {
  socket?.emit(ACCUSATION_SOCKET.vote, { sessionId, vote });
}

export function emitAccusationPick(
  socket: Socket | null,
  sessionId: string,
  killer_instance_id: string
): void {
  socket?.emit(ACCUSATION_SOCKET.pick, { sessionId, killer_instance_id });
}

export function emitAccusationAdopt(
  socket: Socket | null,
  sessionId: string,
  fromUserId: string
): void {
  socket?.emit(ACCUSATION_SOCKET.adopt, { sessionId, fromUserId });
}

export function emitAccusationWarrant(socket: Socket | null, sessionId: string): void {
  socket?.emit(ACCUSATION_SOCKET.warrant, { sessionId });
}
